import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Platform, View } from 'react-native';

import { AddressTypes, useResponsive, VStack, useToggleState } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import {
  OrderStatus,
  useHasEarnedLoyaltyPoints,
  useOrderStatusData,
  useLateOrderNoticeBanner,
} from '@order/features-order-status';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useContactUsHandler } from '@order/shared-hooks';
import { ignoreTimezone } from '@order/utils';

import {
  AppFooter,
  ChallengeCompletionModal,
  LoadingAnimation,
  NotFoundView,
  SignedOutView,
  OrderCancellationModal,
  useOrderCancellationModal,
} from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useCustomer } from '../../context/Customer';
import { useDeliveryOrderInFlight, useGlobalContent } from '../../hooks';
import { useReorderFromOrder } from '../ReorderingScreen';
import { OrderStatusLineItems } from './components';

export const OrderStatusScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) return <LoadingAnimation />;

  if (!isLoggedIn) return <SignedOutView />;

  return <OrderStatusScreenSignedIn />;
};

const OrderStatusScreenSignedIn = () => {
  const isLoggedIn = useIsLoggedIn();
  const { customer } = useCustomer();

  const data = useOrderStatusData({ isLoggedIn, customer });

  const onReorder = useReorderFromOrder(data.order);

  useDeliveryOrderInFlight(); // results later used from cache.

  const { isRenderedOnScreen, isFetching, isInvalidOrderStatus, celebration } = data;

  if (isInvalidOrderStatus) return <NotFoundView />;

  if (isFetching) return <LoadingAnimation />;

  return (
    <>
      <OrderStatusScreenReady {...data} onReorder={onReorder} />

      {celebration && isRenderedOnScreen ? (
        <ChallengeCompletionModal challengeCompletionDetails={celebration} />
      ) : null}
    </>
  );
};

const OrderStatusScreenReady = (
  props: ReturnType<typeof useOrderStatusData> & { readonly onReorder: () => void },
) => {
  const {
    reorderOrder,
    hasDeliveryAddress,
    deliveryAddressName,
    deliveryAddressStreet,
    deliveryAddressSecondaryStreet,
    deliveryAddressCity,
    deliveryAddressState,
    deliveryAddressZipCode,
    deliveryAddressNotes,
    deliveryAddressType,
    deliveryAddressLatitude,
    deliveryAddressLongitude,
    deliveryEstimatedTime,
    deliveryPreference,
    locationName,
    locationAddress,
    locationCity,
    locationState,
    locationZipCode,
    locationLatitude,
    locationLongitude,
    dropoffLocation,
    orderId,
    orderType,
    orderStatus,
    orderTotal,
    orderWantedTime,
    orderLedger,
    orderLineItems,
    payments,
    isLate,
    isPast,
    isPendingFeedback = false,
    isTrackable = false,
    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,
    vendorId,
    canCancel,
    customerName,
    customerLatitude,
    customerLongitude,
    courierName,
    courierPhone,
    courierLatitude,
    courierLongitude,
    restaurantId,
    momoSurveyUrl,
    onReorder,
    refetchOrderStatus,
  } = props;

  const router = useRouter();
  const { currentBreakpoint } = useResponsive();

  const isBrandIconEnabled = useFeatureFlag('CELS-5381-order-is-brand-icon-enabled');
  const { handleContactUs } = useContactUsHandler('order-status');
  const { fields: globalFields } = useGlobalContent();

  const isInStoreOrder = orderType === AddressTypes['in-store'];
  const isOrderCancelled = orderStatus === 'failed';
  const canReorder = !isInStoreOrder && (isOrderCancelled || isPast);

  const isInStoreOrderRateable = Boolean(momoSurveyUrl);
  const canRateDigitalOrInStore = isPendingFeedback || isInStoreOrderRateable;
  const canRateOrder = !isOrderCancelled && isPast && canRateDigitalOrInStore;

  const hasEarnedLoyaltyPoints = useHasEarnedLoyaltyPoints(true, orderId, isOrderCancelled);

  const shouldShowCourierInfo =
    orderType === 'delivery' &&
    orderStatus === FlattenedOrderStatuses.Delivering &&
    courierName &&
    courierPhone;

  const shouldShowAddress = orderType !== 'delivery' || hasDeliveryAddress;

  const shouldShowPayments = payments.length > 0;

  const shouldRenderOrderToHealthKit =
    isOrderToHealthKitEnabled &&
    orderToHealthKitLineItems?.length !== undefined &&
    orderWantedTime !== undefined &&
    Platform.OS === 'ios';

  const shouldDisplayInAppCancellationModal =
    canCancel && orderId !== undefined && restaurantId !== undefined;

  const brandIconSrc = globalFields.brandIcon?.fields.file?.url;
  const brandIconAlt = globalFields.brandIcon?.fields.description;

  useLateOrderNoticeBanner({ isLate });

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: shouldHideCancelDuringAsyncCancelation,
    toggleOn: hideCancelDuringAsyncCancelation,
  } = useToggleState();

  const {
    isOrderCancellationModalVisible,
    showOrderCancellationModal,
    hideOrderCancellationModal,
  } = useOrderCancellationModal();

  // ─── Callbacks ───────────────────────────────────────────────────────────────────

  const handleNavigateToRateOrderScreen = useCallback(() => {
    router.navigate({
      pathname: '/feedback/[orderId]',
      params: { orderId },
    });
  }, [router, orderId]);

  // ─────────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <>
        <OrderStatus.Layout.ContainerWrapper>
          <OrderStatus.Layout.Container>
            <OrderStatus.Layout.Info>
              <OrderStatus.Info
                orderId={orderId}
                vendorId={vendorId}
                orderType={orderType}
                orderStatus={orderStatus}
                wantedTime={orderWantedTime}
                estimatedDeliveryTime={deliveryEstimatedTime}
                orderTotal={orderTotal}
                locationName={locationName}
                locationAddress={locationAddress}
                isTrackable={isTrackable}
                isPendingFeedback={canRateOrder}
                momoSurveyUrl={momoSurveyUrl}
                canReorder={canReorder}
                isPast={isPast}
                brandIconSrc={isBrandIconEnabled ? brandIconSrc : undefined}
                brandIconAlt={isBrandIconEnabled ? brandIconAlt : undefined}
                onReorder={onReorder}
              />
            </OrderStatus.Layout.Info>

            <OrderStatus.Layout.Map>
              <OrderStatus.Map
                orderType={orderType}
                orderStatus={orderStatus}
                locationName={locationName}
                locationLatitude={locationLatitude}
                locationLongitude={locationLongitude}
                customerName={customerName}
                customerLatitude={customerLatitude}
                customerLongitude={customerLongitude}
                hasDeliveryAddress={hasDeliveryAddress}
                deliveryAddressName={deliveryAddressName}
                deliveryAddressType={deliveryAddressType}
                deliveryAddressLatitude={deliveryAddressLatitude}
                deliveryAddressLongitude={deliveryAddressLongitude}
                courierName={courierName}
                courierLatitude={courierLatitude}
                courierLongitude={courierLongitude}
              />
            </OrderStatus.Layout.Map>

            <OrderStatus.Layout.Content>
              <VStack gap={0} divider={<OrderStatus.Divider />}>
                {hasEarnedLoyaltyPoints ? <OrderStatus.LoyaltyPointsBanner /> : null}

                {shouldShowCourierInfo ? (
                  <OrderStatus.CourierInfo
                    orderType={orderType}
                    orderStatus={orderStatus}
                    courierName={courierName}
                    courierPhone={courierPhone}
                  />
                ) : null}

                {shouldShowAddress ? (
                  <OrderStatus.Address
                    orderType={orderType}
                    deliveryAddressName={deliveryAddressName}
                    deliveryAddressStreet={deliveryAddressStreet}
                    deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
                    deliveryAddressCity={deliveryAddressCity}
                    deliveryAddressState={deliveryAddressState}
                    deliveryAddressZipCode={deliveryAddressZipCode}
                    deliveryAddressNotes={deliveryAddressNotes}
                    deliveryPreference={deliveryPreference}
                    locationName={locationName}
                    locationCity={locationCity}
                    locationState={locationState}
                    locationZipCode={locationZipCode}
                    locationAddress={locationAddress}
                    dropoffLocation={dropoffLocation}
                  />
                ) : null}

                <View>
                  <OrderStatusLineItems order={reorderOrder} lineItems={orderLineItems} />

                  <OrderStatus.Ledger ledger={orderLedger} />
                </View>

                {shouldShowPayments ? <OrderStatus.Payments payments={payments} /> : null}

                {shouldRenderOrderToHealthKit ? (
                  <OrderStatus.OrderToHealthKit
                    lineItems={orderToHealthKitLineItems}
                    orderWantedTime={ignoreTimezone(orderWantedTime)}
                  />
                ) : null}
              </VStack>

              <OrderStatus.ContactUs
                orderId={orderId}
                canCancel={canCancel}
                restaurantId={restaurantId}
                onContactUs={handleContactUs}
                onCancelOrder={showOrderCancellationModal}
                shouldHideCancelOption={shouldHideCancelDuringAsyncCancelation}
              />

              {shouldDisplayInAppCancellationModal ? (
                <OrderCancellationModal
                  isVisible={isOrderCancellationModalVisible}
                  orderId={orderId}
                  restaurantId={restaurantId}
                  onSuccess={refetchOrderStatus}
                  onAsyncCancelation={hideCancelDuringAsyncCancelation}
                  onRequestClose={hideOrderCancellationModal}
                />
              ) : null}
            </OrderStatus.Layout.Content>
          </OrderStatus.Layout.Container>
        </OrderStatus.Layout.ContainerWrapper>

        {canReorder || canRateOrder ? (
          <OrderStatus.Layout.Footer>
            <OrderStatus.Ctas
              orderId={orderId}
              orderTotal={orderTotal}
              isPendingFeedback={canRateOrder}
              momoSurveyUrl={momoSurveyUrl}
              canReorder={canReorder}
              onReorder={onReorder}
              onRateOrder={handleNavigateToRateOrderScreen}
            />
          </OrderStatus.Layout.Footer>
        ) : null}
      </>
    );
  }

  return (
    <OrderStatus.Layout.ContainerWrapper>
      <OrderStatus.Layout.Container>
        <OrderStatus.Layout.Map>
          <OrderStatus.Map
            orderType={orderType}
            orderStatus={orderStatus}
            locationName={locationName}
            locationLatitude={locationLatitude}
            locationLongitude={locationLongitude}
            customerName={customerName}
            customerLatitude={customerLatitude}
            customerLongitude={customerLongitude}
            hasDeliveryAddress={hasDeliveryAddress}
            deliveryAddressName={deliveryAddressName}
            deliveryAddressType={deliveryAddressType}
            deliveryAddressLatitude={deliveryAddressLatitude}
            deliveryAddressLongitude={deliveryAddressLongitude}
            courierName={courierName}
            courierLatitude={courierLatitude}
            courierLongitude={courierLongitude}
          />
        </OrderStatus.Layout.Map>

        <OrderStatus.Layout.SidePanel>
          <OrderStatus.Layout.Info>
            <OrderStatus.Info
              orderId={orderId}
              vendorId={vendorId}
              orderType={orderType}
              orderStatus={orderStatus}
              wantedTime={orderWantedTime}
              estimatedDeliveryTime={deliveryEstimatedTime}
              orderTotal={orderTotal}
              locationName={locationName}
              locationAddress={locationAddress}
              isTrackable={isTrackable}
              isPendingFeedback={canRateOrder}
              momoSurveyUrl={momoSurveyUrl}
              canReorder={canReorder}
              isPast={isPast}
              brandIconSrc={isBrandIconEnabled ? brandIconSrc : undefined}
              brandIconAlt={isBrandIconEnabled ? brandIconAlt : undefined}
              onReorder={onReorder}
              onRateOrder={handleNavigateToRateOrderScreen}
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Content>
            <VStack gap={0} divider={<OrderStatus.Divider />}>
              {hasEarnedLoyaltyPoints ? <OrderStatus.LoyaltyPointsBanner /> : null}

              {shouldShowCourierInfo ? (
                <OrderStatus.CourierInfo
                  orderType={orderType}
                  orderStatus={orderStatus}
                  courierName={courierName}
                  courierPhone={courierPhone}
                />
              ) : null}

              {shouldShowAddress ? (
                <OrderStatus.Address
                  orderType={orderType}
                  deliveryAddressName={deliveryAddressName}
                  deliveryAddressStreet={deliveryAddressStreet}
                  deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
                  deliveryAddressCity={deliveryAddressCity}
                  deliveryAddressState={deliveryAddressState}
                  deliveryAddressZipCode={deliveryAddressZipCode}
                  deliveryAddressNotes={deliveryAddressNotes}
                  deliveryPreference={deliveryPreference}
                  locationName={locationName}
                  locationCity={locationCity}
                  locationState={locationState}
                  locationZipCode={locationZipCode}
                  locationAddress={locationAddress}
                  dropoffLocation={dropoffLocation}
                />
              ) : null}

              <View>
                <OrderStatusLineItems order={reorderOrder} lineItems={orderLineItems} />

                <OrderStatus.Ledger ledger={orderLedger} />
              </View>

              {shouldShowPayments ? <OrderStatus.Payments payments={payments} /> : null}

              {shouldRenderOrderToHealthKit ? (
                <OrderStatus.OrderToHealthKit
                  lineItems={orderToHealthKitLineItems}
                  orderWantedTime={ignoreTimezone(orderWantedTime)}
                />
              ) : null}
            </VStack>

            <OrderStatus.ContactUs
              orderId={orderId}
              canCancel={canCancel}
              restaurantId={restaurantId}
              onContactUs={handleContactUs}
              onCancelOrder={showOrderCancellationModal}
              shouldHideCancelOption={shouldHideCancelDuringAsyncCancelation}
            />

            {shouldDisplayInAppCancellationModal ? (
              <OrderCancellationModal
                isVisible={isOrderCancellationModalVisible}
                orderId={orderId}
                restaurantId={restaurantId}
                onSuccess={refetchOrderStatus}
                onAsyncCancelation={hideCancelDuringAsyncCancelation}
                onRequestClose={hideOrderCancellationModal}
              />
            ) : null}
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.SidePanel>
      </OrderStatus.Layout.Container>

      <AppFooter />
    </OrderStatus.Layout.ContainerWrapper>
  );
};
